import type { CrmDayPoint } from '@chatbot/shared-types';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { CRM_SERIES_COLOR } from '../../lib/chart-colors.ts';
import { mediaMovil } from '../../lib/tendencia.ts';
import { LineaDeTendencia } from './LineaDeTendencia.tsx';
import {
  ChartEmpty,
  ChartTooltip,
  type ChartTooltipProps,
  chartAxisProps,
  chartGridProps,
} from './chart-theme.tsx';

/**
 * Conversaciones nuevas por día.
 * Una sola escala: los mensajes son de otro orden de magnitud, así que viajan
 * en el tooltip en vez de en un segundo eje.
 */
export function ConversationsByDayChart({
  data,
  motivoVacio,
}: {
  data: CrmDayPoint[];
  /** Por qué no hay datos, si no los hay. Ver `motivoDeVacio`. */
  motivoVacio?: string;
}) {
  // La media móvil de 7 días quita el efecto del fin de semana, que en el
  // tenant productivo hace caer la serie de ~300 a 20 y vuelve ilegible
  // cualquier lectura de tendencia.
  const tendencia = mediaMovil(data.map((d) => d.conversations));
  const chartData = data.map((d, i) => ({
    ...d,
    label: d.date.slice(5), // MM-DD
    tendencia: tendencia[i],
  }));

  function renderTooltip(props: ChartTooltipProps<CrmDayPoint>) {
    const messages = props.payload?.[0]?.payload?.messages;
    return (
      <ChartTooltip
        {...props}
        extra={
          messages === undefined
            ? undefined
            : [{ name: 'Mensajes', value: messages.toLocaleString('es-CO') }]
        }
      />
    );
  }

  return (
    <div
      className="h-64 w-full"
      data-testid="chart-conversations-by-day"
      role="img"
      aria-label="Gráfico de barras con las conversaciones nuevas por día en el rango seleccionado"
    >
      {chartData.length === 0 ? (
        <ChartEmpty message={motivoVacio} />
      ) : (
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={chartData} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
            <CartesianGrid {...chartGridProps} />
            <XAxis dataKey="label" {...chartAxisProps} />
            <YAxis allowDecimals={false} {...chartAxisProps} />
            <Tooltip cursor={{ fillOpacity: 0.06 }} content={renderTooltip} />
            <Bar
              dataKey="conversations"
              name="Conversaciones"
              fill={CRM_SERIES_COLOR.conversations}
              radius={[4, 4, 0, 0]}
              maxBarSize={28}
            />
            <LineaDeTendencia />
            {/* Dos series: la leyenda deja de ser opcional — la identidad no
                puede depender sólo del color (regla de accesibilidad). */}
            <Legend iconType="plainline" wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
          </ComposedChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}
